import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Header, Image, Sidebar, Menu, Button, Icon, ButtonGroup } from "semantic-ui-react";
import logo from "../../Ll.png";
import { LanguageSelector } from "../Header/LanguageSelector";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { useTranslation } from "react-i18next";
import i18n from "../../utils/i18n";
import { ProfileMenu } from "../Header/ProfileMenu";
import { SmartSearch } from "./SmartSearch";

export interface Heading {
  id: string;
  text: string;
  tagName: string;
}

const style = {
  sidebar: {
    textAlign: "left",
    paddingTop: "5px",
  },
  iframe: {
    display: "block",
    border: "none",
    padding: "0px 0px 0px 20px",
  },
  menuItem: {
    cursor: "pointer",
    padding: "12px 20px",
    borderBottom: "1px solid #f0f0f0",
    textAlign: "left",
  },
};
interface IProps {
  showLogin: () => void;
}
const DocumentationPage = ({ showLogin }: IProps) => {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [currentSection, setCurrentSection] = useState<string | null>(null);
  const [visible, setVisible] = useState(true);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const storedLang = localStorage.getItem("lang") || i18n.language || "lt";
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handleSidebarScroll = (heading: Heading) => {
    const iframe = document.querySelector("iframe");
    const iframeDocument =
      iframe?.contentDocument || iframe?.contentWindow?.document;

    if (iframeDocument) {
      const targetHeading = Array.from(
        iframeDocument.querySelectorAll(heading.tagName.toLowerCase())
      ).find((el) => el.textContent?.trim() === heading.text.trim());
      targetHeading?.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    fetch(`/docs/${storedLang}/documentation.htm`)
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        const headingElements = doc.querySelectorAll("h1, h2, h3");
        const extractedHeadings = Array.from(headingElements).map((heading) => {
          const element = heading as HTMLElement;
          return {
            id:
              element.id ||
              element.innerText.replace(/\s+/g, "-").toLowerCase(),
            text: element.innerText,
            tagName: element.tagName,
          };
        });

        setHeadings(extractedHeadings);


      });
  }, [storedLang]);

  useEffect(() => {
    if (!visible) return;
    const iframe = iframeRef.current;
    const iframeDocument =
      iframe?.contentDocument || iframe?.contentWindow?.document;

    const handleScroll = () => {
      if (iframeDocument) {
        const scrollPosition =
          iframeDocument.documentElement.scrollTop ||
          iframeDocument.body.scrollTop;
        const current = headings.find((heading) => {
          const element = Array.from(
            iframeDocument.querySelectorAll(heading.tagName.toLowerCase())
          ).find(
            (el) => el.textContent?.trim() === heading.text.trim()
          ) as HTMLElement;
          if (element) {
            const offsetTop = element.offsetTop;
            const offsetHeight = element.offsetHeight;
            return scrollPosition < offsetTop + offsetHeight;
          }
          return false;
        });
        if (current) {
          setCurrentSection(current.text);
        }
      }
    };

    if (iframeDocument) {
      iframeDocument.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (iframeDocument) {
        iframeDocument.removeEventListener("scroll", handleScroll);
      }
    };
  }, [headings, visible]);

  return (
    <div style={{ backgroundColor: "white" }}>
      <div
        className="headerContainer"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          className="header-button-gr"
          style={{ display: "flex", alignItems: "center" }}
        >
          <Link to="/">
            <Header as="h3" style={{ display: "flex", alignItems: "center" }}>
              <Image src={logo} size="medium" verticalAlign="middle" />
              {t("user_manual")}
            </Header>
          </Link>
          <SmartSearch iframeRef={iframeRef} resetTrigger={storedLang} />
        </div>
        <div className="header-button-gr">
          {!isAuthenticated ? (
            <>
              <Button
                basic
                compact
                style={{ marginLeft: 10 }}
                onClick={showLogin}
              >
                <Icon name="user circle outline" color="black" />
                {t("login")}
              </Button>
              <LanguageSelector isAuthed={false} />
            </>
          ) : (
            <ButtonGroup style={{ marginLeft: 5}} basic size="small">
              <ProfileMenu />
            </ButtonGroup>
          )}
        </div>
      </div>
      <Button
        circular
        compact
        icon={visible ? "angle double left" : "angle double right"}
        onClick={() => setVisible(!visible)}
        size="mini"
        style={{
          position: "absolute",
          top: 50,
          left: visible ? (storedLang === "lt" ? 270 : 240) : 20,
          zIndex: 9999,
          backgroundColor: "#ffffff",
          color: "black",
          border: "1px solid #e7e7e8",
          boxShadow: "0 0 7px rgba(34, 36, 38, 0.35)",
        }}
      />
      <Sidebar.Pushable style={{ height: "calc(100vh - 50px)" }}>
        <Sidebar
          as={Menu}
          animation="overlay"
          direction="left"
          icon="labeled"
          vertical
          visible={visible}
          style={style.sidebar}
        >
          {headings.map((heading, index) => (
            <Menu.Item
              key={index}
              style={{
                ...style.menuItem,
                paddingLeft: heading.tagName === "H2" ? "25px" : "35px",
                fontWeight: heading.tagName === "H2" ? "bolder" : "normal",
                backgroundColor:
                  currentSection === heading.text ? "#f0f0f0" : "transparent",
                color: currentSection === heading.text ? "#880015" : "black",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleSidebarScroll(heading);
              }}
            >
              {heading.text}
            </Menu.Item>
          ))}
        </Sidebar>

        <Sidebar.Pusher
          style={{
            height: "100%",
            marginLeft: visible ? "260px" : "35px",
            width: visible ? "calc(100% - 260px)" : "calc(100% - 35px)",
          }}
        >
          <div style={{ height: "100%" }}>
            <iframe
              ref={iframeRef}
              src={`/docs/${storedLang}/documentation.htm`}
              style={style.iframe}
              width="100%"
              height="100%"
              title="documentation"
            ></iframe>
          </div>
        </Sidebar.Pusher>
      </Sidebar.Pushable>
    </div>
  );
};

export default DocumentationPage;
